import { Button, Clickable, Icon } from "@madhive/kit-ui";
import { useAgentChat } from "@madhive/mad-hooks";
import { useMadTracker } from "../../hooks";
import { AgentChatPrompterProps } from "..";
import { css } from "@emotion/react";
import type { AgentName } from "@madhive/mad-sdk";

/**
 * `AgentChat.Prompter` is a component that renders a clickable button which,
 * when clicked, sends a predefined message to an AI chat session.
 * It uses the `useAgentChat` hook to manage the chat session and send messages,
 * and the `useMadTracker` hook to track user interactions for analytics purposes.
 */
function AgentChatPrompter<Agent extends AgentName = AgentName>({
  agent,
  sessionId,
  message,
  context,
  onClick,
  disabled: _disabled,
  className: _className,
  buttonProps,
  children,
  clickableProps = {},
  "aria-label": ariaLabel = `Ask Maverick: ${message}`
}: AgentChatPrompterProps<Agent>) {
  const madTracker = useMadTracker();

  const { sendMessage, session, status } = useAgentChat(agent, sessionId, {
    context
  });

  function handleClick() {
    sendMessage(message);
    onClick?.();
    if (madTracker) {
      madTracker.track("Maverick Agent Prompt", {
        dataAgent: agent,
        dataClientKey: sessionId,
        dataSessionId: session?.id || "",
        dataPromptMessage: message
      });
    }
  }

  const disabled = _disabled || status === "processing";
  const className = `kitPlus-AgentChatPrompter${_className ? ` ${_className}` : ""}`;

  if (buttonProps) {
    return (
      <Button
        {...buttonProps}
        ariaLabel={ariaLabel}
        className={className}
        disabled={disabled}
        onClick={handleClick}
      >
        {children || ""}
      </Button>
    );
  }

  return (
    <Clickable
      {...clickableProps}
      aria-label={ariaLabel}
      className={className}
      disabled={disabled}
      onClick={handleClick}
      css={!children && styles.defaultInline}
    >
      {children || <Icon name="Spark" size="extra-small" />}
    </Clickable>
  );
}

export const styles = {
  defaultInline: css`
    display: inline-flex;

    &:not(:hover),
    &:disabled {
      --icon-fill-color: var(--color-tertiary);
    }
  `
};

export default AgentChatPrompter;
